import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Eraser, PenLine } from 'lucide-react';

interface SignaturePadProps {
  /** Appelé à chaque fin de tracé avec le dataURL PNG ; '' quand le pad est vide. */
  onChange: (dataUrl: string) => void;
  disabled?: boolean;
  /** Hauteur du pad en pixels CSS (défaut 200). */
  height?: number;
}

/**
 * Pad de signature manuscrite (sprint « émargement » — 09/10/2026).
 *
 * - Pointer Events : couvre souris, stylet et tactile avec le même code.
 * - `touchAction: none` empêche le défilement de la page pendant la signature.
 * - Le canvas est mis à l'échelle du devicePixelRatio (tracé net sur mobile).
 * - L'export est un PNG (dataURL) consommé tel quel par l'API d'émargement.
 */
const SignaturePad: React.FC<SignaturePadProps> = ({ onChange, disabled = false, height = 200 }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(false);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const width = rect.width || canvas.clientWidth || 600;

    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }, [height]);

  useEffect(() => {
    setupCanvas();
    const handleResize = () => setupCanvas();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setupCanvas]);

  const pointFromEvent = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas ? canvas.getContext('2d') : null;
    if (!canvas || !ctx) return;

    // Certains environnements (événements synthétiques, pointeurs non actifs) refusent
    // la capture : la signature doit fonctionner dans tous les cas.
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
      /* capture indisponible : on dessine quand même */
    }
    drawingRef.current = true;

    const point = pointFromEvent(event);
    lastPointRef.current = point;

    // Point isolé (un simple « clic » laisse une trace)
    ctx.beginPath();
    ctx.arc(point.x, point.y, 1.1, 0, Math.PI * 2);
    ctx.fillStyle = '#111827';
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);

    event.preventDefault();
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || !drawingRef.current) return;
    const ctx = canvasRef.current ? canvasRef.current.getContext('2d') : null;
    const last = lastPointRef.current;
    if (!ctx || !last) return;

    const point = pointFromEvent(event);
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;

    event.preventDefault();
  };

  const finishStroke = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    setHasInk(true);
    const canvas = canvasRef.current;
    if (canvas) onChange(canvas.toDataURL('image/png'));
  };

  const handleClear = () => {
    if (disabled) return;
    setupCanvas();
    setHasInk(false);
    onChange('');
  };

  return (
    <div className="space-y-2">
      <div className={`relative rounded-lg border-2 border-dashed ${disabled ? 'border-gray-200 bg-gray-50' : 'border-brand-300 bg-white'}`}>
        <canvas
          ref={canvasRef}
          className={`block w-full rounded-lg ${disabled ? 'cursor-not-allowed' : 'cursor-crosshair'}`}
          style={{ touchAction: 'none', height: `${height}px` }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishStroke}
          onPointerCancel={finishStroke}
        />
        {!hasInk && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="flex items-center text-sm text-gray-400">
              <PenLine className="mr-2 h-4 w-4" />
              Signez ici (souris ou doigt)
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Signature manuscrite — conservée en image PNG</p>
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled || !hasInk}
          className="flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
        >
          <Eraser className="mr-2 h-4 w-4" />
          Effacer
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
